import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Countries, Products, BaselineCountryCodes } from '@/comm'
import { postFetcher } from '@/hooks'
import { useRole } from '@/hooks/auth'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { create } from 'zustand'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { countryColumn, pidColumn } from './columns'
import { Role } from '@/constants/role'

const Index = () => {
  const { data: data1 = {} } = useSWR(['/profits/baselines/all'])
  const globalStartDate = useProfitBaseLineStore(
    (state) => state.globalStartDate,
  )
  const setGlobalStartDate = useProfitBaseLineStore(
    (state) => state.setGlobalStartDate,
  )

  const enable = useProfitBaseLineStore((state) => state.enable)
  const showEditor = useProfitBaseLineStore((state) => state.showEditor)

  // 提取所有可用日期
  const availableDates = useMemo(() => {
    if (!data1 || typeof data1 !== 'object') {
      return []
    }
    return Object.keys(data1)
      .filter((key) => key && data1[key])
      .sort()
  }, [data1])

  // 初始化默认日期(选择第一个可用日期)
  useEffect(() => {
    if (availableDates.length > 0 && !globalStartDate) {
      setGlobalStartDate(availableDates[availableDates.length - 1])
    } else if (availableDates.length === 0 && globalStartDate) {
      // 如果没有可用日期，清空 globalStartDate
      setGlobalStartDate('')
    }
  }, [availableDates, globalStartDate, setGlobalStartDate])

  // 根据选择的日期过滤数据
  const data = useMemo(() => {
    if (!globalStartDate || !data1 || !data1[globalStartDate]) {
      // 如果没有选择日期或没有数据，返回空数组结构
      return BaselineCountryCodes.map((country) => ({
        country,
        baselines: {},
      }))
    }
    const selectedData = data1[globalStartDate] || []

    // 将数组转换为按country分组的对象
    const baselinesByCountry = selectedData.reduce((acc, item) => {
      if (!acc[item.country]) {
        acc[item.country] = {}
      }
      acc[item.country][item.pid] = item.value
      return acc
    }, {})

    // 构建表格数据
    return BaselineCountryCodes.map((country) => ({
      country,
      baselines: baselinesByCountry[country],
    }))
  }, [data1, globalStartDate])

  const columns = useMemo(() => {
    const rows = [countryColumn]
    rows.push(
      ...Products.filter((p) => p.group != undefined).map((p) => pidColumn(p)),
    )
    return rows
  }, [])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableMultiRowSelection: true,
  })

  const onCellClick = (cell) => {
    if (!enable) return

    const cellValue = cell.getValue()
    const original = cell.row.original

    // 确保有必要的值才打开编辑器（即使没有 globalStartDate 也可以打开）
    if (cellValue?.pid && original?.country) {
      showEditor({
        pid: cellValue.pid,
        country: original.country,
        before: cellValue.profit,
      })
    }
  }

  return (
    <>
      <EditorDialog />
      <BatchCreateDialog data={data1} />
      <EditButton availableDates={availableDates} />
      <DataTableWithBorder table={table} onCellClick={onCellClick} />
    </>
  )
}

const EditButton = ({ availableDates = [] }) => {
  const isBuyerLeader = useRole(Role.BuyerLeader)
  const {
    enable,
    toggleEnable,
    globalStartDate,
    setGlobalStartDate,
  } = useProfitBaseLineStore()
  if (!isBuyerLeader) return null
  return (
    <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium">Start Date:</label>
        <Select 
          value={globalStartDate || undefined} 
          onValueChange={setGlobalStartDate}
          disabled={availableDates.length === 0}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={availableDates.length === 0 ? "无可用日期" : "选择日期"} />
          </SelectTrigger>
          {availableDates.length > 0 && (
            <SelectContent>
              {availableDates.map((date) => (
                <SelectItem key={date} value={date}>
                  {date}
                </SelectItem>
              ))}
            </SelectContent>
          )}
        </Select>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enable-editor"
          checked={enable}
          onCheckedChange={toggleEnable}
        />
        <label
          htmlFor="terms"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          Enable editor model
        </label>
      </div>
      <div className="flex items-center space-x-2">
        <Button
          onClick={() => useProfitBaseLineStore.getState().showBatchCreate()}
          variant="outline"
        >
          批量创建
        </Button>
      </div>
    </div>
  )
}

export const useProfitBaseLineStore = create((set) => ({
  enable: false,
  showBatchDialog: false,
  batchStartDate: '',
  batchMode: 'empty',
  batchSourceDate: '',
  show: false,
  pid: '',
  country: '',
  before: 0,
  // 全局选择器的值
  globalStartDate: '',
  // 当前编辑记录的值
  editStartDate: '2025-01-01',
  toggleEnable: () => set((state) => ({ enable: !state.enable })),
  setGlobalStartDate: (globalStartDate) => set({ globalStartDate }),
  showBatchCreate: () => set({ showBatchDialog: true }),
  hideBatchCreate: () => set({ showBatchDialog: false }),
  setBatchStartDate: (date) => set({ batchStartDate: date }),
  setBatchMode: (mode) => set({ batchMode: mode }),
  setBatchSourceDate: (date) => set({ batchSourceDate: date }),
  showEditor: ({ pid, country, before }) =>
    set((state) => ({
      pid,
      country,
      before,
      editStartDate: state.globalStartDate || '2025-01-01',
      show: true,
    })),
  hideEditor: () => set({ show: false }),
}))

const FormSchema = z.object({
  profit: z
    .union([z.string(), z.number()])
    .refine((val) => val !== '' && val !== null && val !== undefined, {
      message: 'profit cannot be empty',
    })
    .refine((val) => !isNaN(Number(val)), {
      message: 'profit must be a number',
    }),
  startDate: z.string(),
})

const EditorDialog = () => {
  const { pid, country, before, editStartDate } = useProfitBaseLineStore()

  const show = useProfitBaseLineStore((state) => state.show)
  const { hideEditor } = useProfitBaseLineStore()

  const { trigger } = useSWRMutation('/profits/baselines', postFetcher)
  const onSubmit = (data) => {
    trigger(
      {
        pid,
        country,
        value: Number(data.profit),
        startDate: data.startDate,
      },
      {
        onSuccess: () => {
          hideEditor()
          mutate(['/profits/baselines/all'])
        },
      },
    )
  }

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      profit: before !== undefined ? before : 0,
      startDate: editStartDate,
    },
  })

  const {
    formState: { isValid, isSubmitting },
    setValue,
  } = form

  useEffect(() => {
    setValue('profit', before !== undefined ? before : 0)
    setValue('startDate', editStartDate)
  }, [before, editStartDate, setValue])

  return (
    <Dialog open={show} onOpenChange={hideEditor}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editor profit baseline.</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span>
              Pid:<span className="text-destructive">{pid}</span>
            </span>
            <span>
              Country:<span className="text-destructive">{country}</span>
            </span>
            <span>
              Before:<span className="text-destructive">{before}</span>
            </span>
          </div>
          <div className="grid gap-4 py-4">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Start Date</Label>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="profit"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Profit</Label>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogClose asChild>
                  <Button disabled={!isValid || isSubmitting} type="submit">
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </DialogClose>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default Index

const BatchCreateDialog = ({ data = {} }) => {
  const { 
    showBatchDialog, 
    batchStartDate, 
    batchMode, 
    batchSourceDate, 
    hideBatchCreate, 
    setBatchStartDate, 
    setBatchMode, 
    setBatchSourceDate 
  } = useProfitBaseLineStore()
  const { trigger, isMutating } = useSWRMutation('/profits/baselines/batch', postFetcher)

  const handleSubmit = () => {
    if (!batchStartDate) {
      alert('请选择开始日期')
      return
    }
    if (batchMode === 'copy' && !batchSourceDate) {
      alert('请选择复制源日期')
      return
    }
    trigger(
      { startDate: batchStartDate, mode: batchMode, sourceDate: batchSourceDate },
      {
        onSuccess: () => {
          hideBatchCreate()
          mutate(['/profits/baselines/all'])
          alert('批量创建成功!')
        },
        onError: (error) => {
          alert('创建失败: ' + (error.message || '未知错误'))
        }
      }
    )
  }

  // Filter available dates
  const availableDates = useMemo(() => {
    return Object.keys(data).filter(date => {
      const dayData = data[date]
      return dayData && dayData.length > 0
    }).sort()
  }, [data])

  // Effect to set default source date if available
  useEffect(() => {
    if (availableDates.length > 0) {
      if (!batchSourceDate || !availableDates.includes(batchSourceDate)) {
        setBatchSourceDate(availableDates[availableDates.length - 1])
      }
    } else {
      setBatchSourceDate('')
    }
  }, [availableDates, batchSourceDate, setBatchSourceDate])

  return (
    <Dialog open={showBatchDialog} onOpenChange={hideBatchCreate}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>批量创建Profit Baseline</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Start Date</Label>
            <Input type="date" value={batchStartDate} onChange={(e) => setBatchStartDate(e.target.value)} />
          </div>

          <div className="flex flex-col gap-2">
            <Label>创建模式</Label>
            <div className="flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <input
                  type="radio"
                  id="empty"
                  name="batchMode"
                  value="empty"
                  checked={batchMode === 'empty'}
                  onChange={(e) => setBatchMode(e.target.value)}
                  className="h-4 w-4"
                />
                <Label htmlFor="empty" className="font-normal cursor-pointer">空数据创建(默认: pid=1, country=BR, value=0)</Label>
              </div>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="copy"
                    name="batchMode"
                    value="copy"
                    checked={batchMode === 'copy'}
                    onChange={(e) => setBatchMode(e.target.value)}
                    disabled={availableDates.length === 0}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="copy" className={`font-normal cursor-pointer ${availableDates.length === 0 ? 'text-gray-400' : ''}`}>
                    复制指定日期的数据 {availableDates.length === 0 && '(无可用数据)'}
                  </Label>
                </div>
                {batchMode === 'copy' && (
                  <div className="pl-6">
                    <Select value={batchSourceDate} onValueChange={setBatchSourceDate}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="选择复制源日期" />
                      </SelectTrigger>
                      {availableDates.length > 0 && (
                        <SelectContent>
                          {availableDates.map(date => (
                            <SelectItem key={date} value={date}>{date}</SelectItem>
                          ))}
                        </SelectContent>
                      )}
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={isMutating || !batchStartDate}>
            {isMutating ? '创建中...' : '创建'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
